import type { ReactNode } from 'react';

import {
  BLOCKS,
  MARKS,
  type Document,
  type Node,
} from '@contentful/rich-text-types';
import BpkInfoBanner, {
  ALERT_TYPES as NEW_ALERT_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-info-banner';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import RichText from '../RichText';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type { SkyscannerContentapiV2CommonCommonTypes } from '@skyscanner-internal/landing-pages-api-web-http-client';
import type { HotelsIndicativePricesLegalDisclaimerProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/HotelsIndicativePricesLegalDisclaimer';

import STYLES from './HotelsIndicativePricesLegalDisclaimer.module.scss';

type RichTextProps = SkyscannerContentapiV2CommonCommonTypes.RichText;
const isDocument = (item: unknown): item is Document =>
  typeof item === 'object' &&
  item !== null &&
  'nodeType' in item &&
  'content' in item &&
  'data' in item;

const localOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: Node, children: ReactNode) => (
      <BpkText tagName="span" textStyle={TEXT_STYLES.sm}>
        {children}
      </BpkText>
    ),
  },
  renderMark: {
    // Change from 'bold' to 'strong' to match the new disclaimer format
    [MARKS.BOLD]: (text: any) => <strong>{text}</strong>,
  },
};

const HotelsIndicativePricesLegalDisclaimer = ({
  desktopDisclaimer,
  fullWidth = true,
  isChild = false,
  mobileDisclaimer,
  useDangerouslySetInnerHtml = false,
}: HotelsIndicativePricesLegalDisclaimerProps & {
  isChild?: boolean;
}) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const desktopValue = desktopDisclaimer ?? '';
  const mobileValue = mobileDisclaimer ?? '';

  function renderDisclaimerContent(content: string | Document | RichTextProps) {
    // Case 1: Content is a wrapped JSON string object (e.g. { documentJsonValue: string }) — parse as Document
    if (
      content &&
      typeof content === 'object' &&
      'documentJsonValue' in content &&
      typeof content.documentJsonValue === 'string'
    ) {
      try {
        const doc = JSON.parse(content.documentJsonValue);
        if (isDocument(doc)) {
          return <RichText body={doc} options={localOptions} />;
        }
      } catch (e) {
        return null;
      }
    }

    // Case 2: Content is a plain string (plain text or HTML)
    if (typeof content === 'string') {
      if (useDangerouslySetInnerHtml) {
        /* eslint-disable-next-line react/no-danger */
        return <span dangerouslySetInnerHTML={{ __html: content }} />;
      }
      return content;
    }

    // Case 3: Content is already a Contentful Document object
    if (isDocument(content)) {
      return <RichText body={content} options={localOptions} />;
    }
    return null;
  }

  return (
    <div
      className={classNames(STYLES.HotelsIndicativePricesLegalDisclaimer, {
        [STYLES.HotelsIndicativePricesLegalDisclaimer__fullWidth]: fullWidth,
      })}
      data-testid="indicative-prices-legal-disclaimer-section"
      {...(isChild
        ? {}
        : {
            'data-tracking-element-id': rawComponentName,
            'data-tracking-common-params': buildMBDParam(rawComponentName),
          })}
    >
      <BpkInfoBanner
        type={NEW_ALERT_TYPES.INFO}
        message={
          <div>
            <div className={STYLES.AboutSearchResults__desktopBanner}>
              {renderDisclaimerContent(desktopValue)}
            </div>

            <div className={STYLES.AboutSearchResults__mobileBanner}>
              {renderDisclaimerContent(mobileValue)}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default HotelsIndicativePricesLegalDisclaimer;
